/**
 * Modal: release a deployed network to Agentic Networks.
 *
 * Two modes (step 2.2):
 *   - NEW_POOL: creates a brand-new customer pool at the next release version.
 *   - PATCH_EXISTING_POOLS: advances the release pointer on selected pools
 *     in place. Members stay; running conversations continue.
 *
 * All decision logic lives in ``utils/releaseDialog.js`` so it's unit-tested
 * without React — the JSX below is a thin shell.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';
import { releaseNetwork, listNetworkPools } from '@/services/agenticNetworksApi';
import { listNetworkReleases, getNextReleaseLabel } from '@stepflow/ui-shared/services/networkReleasesApi';
import { listNetworkSnapshots } from '@/services/networkRestoreApi';
import VersionSnapshotPicker from './VersionSnapshotPicker.jsx';
import {
  RELEASE_MODE_NEW_POOL,
  RELEASE_MODE_PATCH_EXISTING_POOLS,
  availableReleaseModes,
  canSubmitRelease,
  composeReleaseSubmitPayload,
  patchablePools,
  pickDefaultReleaseMode,
} from '@/utils/releaseDialog';
import {
  DEFAULT_TRIGGER_MODE,
  listTriggerModes,
} from '@stepflow/ui-shared/utils/networkTrigger.js';
import { displayVersionLabel, predictNextVersionLabel } from '@stepflow/ui-shared/utils/stepflowVersionLabel';
import { WORKBENCH_OUTLINE_BTN_MODAL_LG } from '@/utils/workbenchOutlineButtonClassNames.js';
import ReleaseVersionHistory from './ReleaseVersionHistory';
import SupervisorMemberPicker from './SupervisorMemberPicker.jsx';
import CollapsibleSection from '@stepflow/ui-shared/components/CollapsibleSection.jsx';
import WorkspaceMembersMultiPicker from './WorkspaceMembersMultiPicker.jsx';

// Default room-name suggestion pool. Large + varied so the pre-filled name rarely
// collides with an existing room on the network (the real uniqueness guard is the
// backend 409; this just makes the first suggestion usually free). Backend mirror:
// api/pet_names.py::_ANIMAL_FIRST_NAMES.
const DEFAULT_POOL_SUGGESTIONS = [
  'luca', 'lara', 'milo', 'nora', 'kai', 'zia', 'bella', 'nala', 'simba', 'remy',
  'koda', 'kira', 'loki', 'nova', 'atlas', 'bowie', 'cleo', 'dara', 'echo', 'finn',
  'greta', 'hazel', 'indy', 'juno', 'leo', 'mila', 'nico', 'ollie', 'pippa', 'quinn',
  'rio', 'stella', 'tala', 'uma', 'vera', 'willa', 'xena', 'yuki', 'zara', 'archie',
  'basil', 'cosmo', 'daisy', 'elsa', 'freya', 'gus', 'honey', 'ivy', 'jax', 'kona',
  'lily', 'maple', 'nori', 'onyx', 'pearl', 'ruby', 'sage', 'theo', 'ursa', 'wally',
  'zuri', 'amber', 'biscuit', 'clover', 'dash', 'ember', 'ginger', 'hero', 'kenji', 'lotus',
  'misty', 'nimbus', 'opal', 'percy', 'quill', 'rocket', 'sky', 'tango', 'viola', 'willow',
  'xander', 'yarrow', 'ziggy', 'aspen', 'birch', 'cedar', 'dune', 'fern', 'haven', 'iris',
  'jasper', 'koa', 'lyra', 'mika', 'orion', 'poppy', 'reef', 'tess', 'vale', 'zephyr',
  'arlo', 'bruno', 'coco', 'duke', 'enzo', 'gizmo', 'hugo', 'jett', 'luna', 'otis',
  'penny', 'rex', 'sadie', 'toby', 'winnie', 'zeke', 'juniper', 'marlow', 'silas', 'wilder',
];

function pickDefaultPoolName() {
  return DEFAULT_POOL_SUGGESTIONS[Math.floor(Math.random() * DEFAULT_POOL_SUGGESTIONS.length)];
}

export default function ReleaseNetworkDialog({
  open,
  onClose,
  networkId,
  networkName,
  networkReleaseVersion,
  network,
  onReleased,
  // Optional hint from the caller about the nature of the last build:
  // ``'structural'`` (roster/topology/edges → new SFN → MAJOR) vs
  // ``'prompt-refresh'`` (re-render prompts only → same SFN → MINOR). See
  // §2.2. Used ONLY for the pre-server prediction placeholder; the
  // authoritative label from GET /next-release-label always wins once it
  // lands (it runs a byte-for-byte SFN compare). When absent, we predict
  // the conservative MINOR (the common config-only case) so a prompt-refresh
  // never flashes a scary "2.00" that then drops back to "1.10".
  changeType,
}) {
  const { user, activeTenantId } = useAuth();
  const currentUserEmail = (user?.email || '').trim().toLowerCase();

  const [pools, setPools] = useState([]);
  const [poolsLoading, setPoolsLoading] = useState(false);
  const [poolsError, setPoolsError] = useState(null);
  const [mode, setMode] = useState(RELEASE_MODE_NEW_POOL);
  const [poolName, setPoolName] = useState('');
  const [selectedPoolIds, setSelectedPoolIds] = useState([]);
  // NEW_POOL governance triplet (Phase A): owner + co-supervisors + trigger.
  // The owner defaults to the releaser (familiar Workbench pattern: "I am
  // also the supervisor unless I say otherwise"); supervisors stays empty
  // by default; trigger defaults to HYBRID — the most permissive mode and
  // what the system did implicitly before this field existed.
  const [ownerEmail, setOwnerEmail] = useState('');
  const [supervisorEmails, setSupervisorEmails] = useState([]);
  const [triggerMode, setTriggerMode] = useState(DEFAULT_TRIGGER_MODE);
  // Pre-bake the network's skills into a workspace for the new room (default ON).
  // Backend also gates on builder-present + skills-exist, so this is a no-op when
  // there's nothing to bake (doc/arch/vertical/SKILLS.md).
  const [bootstrapWorkspace, setBootstrapWorkspace] = useState(true);
  // Seed this room's own connection instances (MCP shells) mirroring the network's
  // tools 1:1 (shared → room-wide, per-agent → per-agent), so they show in the
  // room's Connections tab to credential. Default on. Backend flag: seedRoomTools.
  const [seedRoomTools, setSeedRoomTools] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // NEW_POOL form is structured as collapsible sections mirroring the room
  // settings drawer (Members / Trigger mode / Room context). "Basics" (Version
  // + Pool name) opens expanded; governance + tools sections start collapsed
  // and pre-filled (most releases keep the defaults — owner=releaser,
  // trigger=HYBRID, workspace+connections on). The user expands only what they
  // want to change. ``id`` = drawer section key so the wording stays in sync.
  const [openSection, setOpenSection] = useState({
    basics: true,
    members: false,
    trigger: false,
    roomContext: false,
  });
  const toggleSection = (key) =>
    setOpenSection((s) => ({ ...s, [key]: !s[key] }));

  // Version + Snapshot picker state. Loaded in parallel with pools when
  // the dialog opens. ``versionRef`` is ``"vN"`` (or empty when the
  // network has no releases yet — the backend cuts the next version).
  // ``snapshotRef`` is the synthetic ``"{kind}::{capturedAt}"`` key.
  const [versions, setVersions] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState(null);
  const [vspValue, setVspValue] = useState({ versionRef: '', snapshotRef: '' });
  const [nextLabelFromServer, setNextLabelFromServer] = useState(null);

  const triggerOptions = useMemo(() => listTriggerModes(), []);

  // The workspace bootstrap only applies to a network with a builder agent — a
  // workspace is the builder's on-disk tree (doc/arch/vertical/SKILLS.md). Pure
  // worker networks have no builder (and we don't author skills for them), so
  // hide the checkbox entirely there. ``network.agents`` is a {logicalId: cfg}
  // map; UNIVERSAL_BUILDER is the builder type.
  const hasBuilderAgent = useMemo(() => {
    const agents = (network && typeof network.agents === 'object') ? network.agents : {};
    return Object.values(agents || {}).some(
      (a) => String((a || {}).type || '').toUpperCase() === 'UNIVERSAL_BUILDER',
    );
  }, [network]);

  // Load existing pools + Versions + Snapshots on open. The three
  // requests are independent so we fire them in parallel; each
  // updates its own slice of state and the dialog tolerates partial
  // load (e.g. snapshots fail → picker shows "no snapshots" but the
  // pool flow still works).
  useEffect(() => {
    if (!open || !networkId) return undefined;
    let cancelled = false;
    setPoolsLoading(true);
    setPoolsError(null);
    setPoolName(pickDefaultPoolName());
    setSelectedPoolIds([]);
    setError(null);
    setOwnerEmail(currentUserEmail);
    setSupervisorEmails([]);
    setTriggerMode(DEFAULT_TRIGGER_MODE);
    setVersions([]);
    setSnapshots([]);
    setVersionsLoading(true);
    setVersionsError(null);
    setVspValue({ versionRef: '', snapshotRef: '' });
    setNextLabelFromServer(null);
    getNextReleaseLabel(networkId)
      .then((res) => {
        if (cancelled) return;
        if (res && typeof res.label === 'string' && res.label) {
          setNextLabelFromServer({ label: res.label, kind: res.kind });
        }
      })
      .catch(() => { /* fall back to local prediction */ });

    listNetworkPools(networkId)
      .then(({ pools: fetched }) => {
        if (cancelled) return;
        setPools(fetched);
        setMode(pickDefaultReleaseMode(fetched));
      })
      .catch((err) => {
        if (cancelled) return;
        setPoolsError(err.message || 'Failed to load pools');
        setPools([]);
        setMode(RELEASE_MODE_NEW_POOL);
      })
      .finally(() => {
        if (!cancelled) setPoolsLoading(false);
      });

    // Section 3: fetch the Version + Snapshot lists in parallel. The
    // releases endpoint returns active + deactivated rows; the picker
    // filters out deactivated. The snapshots endpoint returns the
    // unified baseline / release-baseline / user / auto rows already
    // tagged with ``parentVersion`` (Section 2).
    Promise.allSettled([
      listNetworkReleases(networkId, { limit: 50 }),
      listNetworkSnapshots(networkId),
    ]).then(([rRes, sRes]) => {
      if (cancelled) return;
      if (rRes.status === 'fulfilled') {
        setVersions(Array.isArray(rRes.value?.releases) ? rRes.value.releases : []);
      } else {
        setVersionsError(rRes.reason?.message || 'Failed to load Versions');
      }
      if (sRes.status === 'fulfilled') {
        setSnapshots(Array.isArray(sRes.value?.snapshots) ? sRes.value.snapshots : []);
      }
      setVersionsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [open, networkId, currentUserEmail]);

  const modes = useMemo(() => availableReleaseModes(pools), [pools]);
  // The next release version targets current + 1; use null if unknown so
  // the "behind" indicator gracefully degrades to "always patchable".
  const nextVersion = Number.isFinite(networkReleaseVersion)
    ? Number(networkReleaseVersion) + 1
    : null;
  // The version-picker needs a concrete vN+1 even when the network has
  // no releases yet (so "Cut new (v1)" is always offerable). Derive from
  // the loaded ``versions`` list, falling back to ``nextVersion`` from
  // the network row, then to 1.
  const nextVersionForPicker = useMemo(() => {
    const max = (versions || [])
      .map((r) => Number(r?.releaseVersion))
      .filter((n) => Number.isFinite(n) && n >= 1)
      .reduce((a, b) => Math.max(a, b), 0);
    if (max >= 1) return max + 1;
    if (Number.isFinite(nextVersion) && Number(nextVersion) >= 1) return Number(nextVersion);
    return 1;
  }, [versions, nextVersion]);
  const isNoop = nextLabelFromServer?.kind === 'NOOP';
  // Pre-server prediction only. ``releaseEligible`` is coarse — it's true for
  // ANY pending edit, so on its own it can't tell a prompt-refresh (same SFN
  // → MINOR) from a structural revise (new SFN → MAJOR). When the caller
  // knows the change kind we honor it exactly; otherwise we keep the old
  // coarse heuristic. Either way the authoritative label from
  // GET /next-release-label (byte-for-byte SFN compare) wins once it lands.
  // §4.1 Bug C.
  const normalizedChangeType = String(changeType || '').trim().toLowerCase();
  const topologyChanged = (() => {
    if (isNoop) return false;
    if (normalizedChangeType === 'prompt-refresh') return false; // same SFN → MINOR
    if (normalizedChangeType === 'structural') return true;      // new SFN → MAJOR
    return network?.releaseEligible !== false;                   // unknown → coarse heuristic
  })();
  const nextVersionLabel = useMemo(() => {
    if (nextLabelFromServer?.label) return nextLabelFromServer.label;
    return predictNextVersionLabel(versions, topologyChanged);
  }, [nextLabelFromServer, versions, topologyChanged]);

  // Highest already-released version (active + deactivated). This is the
  // target for the "upgrade a room to an existing newer version" path — the
  // first-ever MAJOR-upgrade case (e.g. room on 1.00 while 2.00 already
  // exists). See doc/arch/vertical/NETWORK-TO-ROOMS.md §4.1.
  const maxExisting = useMemo(() => (versions || [])
    .map((r) => Number(r?.releaseVersion))
    .filter((n) => Number.isFinite(n) && n >= 1)
    .reduce((a, b) => Math.max(a, b), 0), [versions]);

  // Can we cut a genuinely NEW version? Only when the server says something
  // changed (not NOOP) and the network row has pending changes. When we
  // CAN'T, PATCH mode falls back to advancing behind rooms to ``maxExisting``.
  const hasPendingReleaseChanges = !isNoop && network?.pendingReleaseChanges !== false;
  const patchTargetVersion = hasPendingReleaseChanges
    ? nextVersion
    : (maxExisting >= 1 ? maxExisting : nextVersion);
  const patchTargets = useMemo(
    () => patchablePools(pools, patchTargetVersion),
    [pools, patchTargetVersion],
  );

  // Label for whatever the patch will land the rooms on: the predicted next
  // label when cutting new, else the existing target version's label.
  const patchTargetLabel = hasPendingReleaseChanges
    ? nextVersionLabel
    : (displayVersionLabel(patchTargetVersion, versions) || nextVersionLabel);

  // Are we upgrading selected rooms to an ALREADY-existing version? True only
  // when we can't cut new AND at least one SELECTED room is behind the
  // existing target. This opens the otherwise-disabled Release button for the
  // MAJOR-upgrade case without loosening the no-op guard for fresh cuts.
  const selectedBehindExisting = !hasPendingReleaseChanges
    && maxExisting >= 1
    && patchTargets.some((p) => selectedPoolIds.includes(p.poolId) && p.isBehind);

  const submitDecision = canSubmitRelease({
    mode,
    poolName,
    selectedPoolIds,
    availablePools: pools,
    // Server is authoritative: if it ran live SFN-hash + agent-config-hash
    // compare and decided "nothing changed", PATCH must be blocked even
    // when ``pendingReleaseChanges`` is stale-true on the network row —
    // UNLESS we're upgrading a behind room to an existing newer version.
    hasPendingReleaseChanges,
    isNoop,
    isUpgradeToExisting: selectedBehindExisting,
  });

  const togglePoolSelection = (poolId) => {
    setSelectedPoolIds((prev) => (
      prev.includes(poolId) ? prev.filter((p) => p !== poolId) : [...prev, poolId]
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    let payload;
    try {
      payload = composeReleaseSubmitPayload({
        mode,
        poolName,
        selectedPoolIds,
        ownerEmail,
        supervisorEmails,
        trigger: triggerMode,
        bootstrapWorkspace,
        seedRoomTools,
        // Upgrade-to-existing-version (MAJOR upgrade): pin the patch to the
        // highest already-released version so the backend reuses its SFN
        // version + ARN instead of cutting a new one.
        isUpgradeToExisting: selectedBehindExisting,
        versionRef: selectedBehindExisting ? `v${maxExisting}` : '',
      });
    } catch (err) {
      setError(err.message);
      return;
    }
    // Section 3: forward the Version + Snapshot picks alongside the
    // existing payload. The backend treats them as advisory in this
    // section (validates shape but still uses the canonical
    // "latest released Version" path); Section 4 wires the picks to
    // the alias retarget / pool ReleaseVersion stamp.
    if (mode === RELEASE_MODE_NEW_POOL && vspValue?.versionRef) {
      payload = {
        ...payload,
        versionRef: vspValue.versionRef,
        ...(vspValue.snapshotRef ? { snapshotRef: vspValue.snapshotRef } : {}),
      };
    }
    setSubmitting(true);
    try {
      const res = await releaseNetwork(networkId, payload);
      // Backend returns ``200 { noop: true }`` when neither topology nor
      // agent config has changed since the previous release AND the request
      // didn't pin a ``versionRef`` to an existing version. Nothing was
      // actually written — we must NEVER report success, in EITHER mode.
      // (Regression: this guard used to be scoped to NEW_POOL, so a PATCH
      // that no-op'd fell through to onReleased()/onClose() and the UI lied
      // about success while the room stayed on its old version.)
      if (res && res.noop === true) {
        setError(
          res.message
          || (mode === RELEASE_MODE_PATCH_EXISTING_POOLS
            ? 'Nothing to apply — the selected room is already on the latest version, or no version gap was detected.'
            : 'No changes since the last release — pick the existing version above to open a new pool against it.'),
        );
        return;
      }
      onReleased?.();
      onClose();
    } catch (err) {
      setError(err.message || 'Release failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <motion.button
            type="button"
            aria-label="Close"
            className="absolute inset-0 bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !submitting && onClose()}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="release-dialog-title"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            className="relative z-[101] max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-gray-200 bg-white p-6 shadow-xl"
          >
            <h2 id="release-dialog-title" className="text-lg font-semibold text-gray-900">
              Create a Room
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              This freezes <span className="font-medium text-gray-800">{networkName}</span> as a versioned
              snapshot.
              {' '}Choose whether to patch existing pools in place or open a new one.
            </p>

            <form onSubmit={handleSubmit} className="mt-4">
              {/* Mode selector — radios, not tabs, to make the invariant
                  (exactly one mode at a time) explicit in the DOM. */}
              <fieldset className="space-y-3" disabled={submitting}>
                <legend className="sr-only">Release mode</legend>
                <label
                  className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${
                    mode === RELEASE_MODE_PATCH_EXISTING_POOLS
                      ? 'border-amber-400 bg-amber-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  } ${!modes.canPatch ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <input
                    type="radio"
                    name="release-mode"
                    value={RELEASE_MODE_PATCH_EXISTING_POOLS}
                    checked={mode === RELEASE_MODE_PATCH_EXISTING_POOLS}
                    disabled={!modes.canPatch}
                    onChange={() => setMode(RELEASE_MODE_PATCH_EXISTING_POOLS)}
                    className="mt-0.5"
                  />
                  <span className="text-sm">
                    <span className="block font-medium text-gray-900">
                      Patch existing pool(s)
                    </span>
                    <span className="block text-gray-600">
                      Advance selected pools to the new version. Members stay; running
                      conversations continue.
                    </span>
                  </span>
                </label>

                <label
                  className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${
                    mode === RELEASE_MODE_NEW_POOL
                      ? 'border-emerald-400 bg-emerald-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="release-mode"
                    value={RELEASE_MODE_NEW_POOL}
                    checked={mode === RELEASE_MODE_NEW_POOL}
                    onChange={() => setMode(RELEASE_MODE_NEW_POOL)}
                    className="mt-0.5"
                  />
                  <span className="text-sm">
                    <span className="block font-medium text-gray-900">
                      Release to a new pool
                    </span>
                    <span className="block text-gray-600">
                      Open a parallel customer room at the new version. Doesn't touch
                      existing pools.
                    </span>
                  </span>
                </label>
              </fieldset>

              {/* Mode-specific body */}
              {mode === RELEASE_MODE_NEW_POOL ? (
                <div className="mt-4 overflow-hidden rounded-lg border border-gray-200">
                  {/* BASICS — Version & Snapshot + Pool name. Expanded by
                      default (the only required input lives here). No drawer
                      equivalent, so it's a plain top section. */}
                  <CollapsibleSection
                    id="basics"
                    title="Basics"
                    subtitle="Version to release · room name"
                    open={openSection.basics}
                    onToggle={() => toggleSection('basics')}
                    summary={poolName ? `${poolName} · ${nextVersionLabel || 'next'}` : 'Name required'}
                  >
                    <div className="space-y-4 py-1">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Version & Snapshot
                        </label>
                        <div className="mt-2">
                          <VersionSnapshotPicker
                            releases={versions}
                            snapshots={snapshots}
                            nextVersion={nextVersionForPicker}
                            value={vspValue}
                            onChange={setVspValue}
                            loading={versionsLoading}
                            error={versionsError}
                            topologyChanged={network?.releaseEligible !== false}
                            nextLabel={nextVersionLabel}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700" htmlFor="poolName">
                          Room name
                        </label>
                        <input
                          id="poolName"
                          type="text"
                          value={poolName}
                          onChange={(ev) => setPoolName(ev.target.value)}
                          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="e.g. luca"
                          autoComplete="off"
                          disabled={submitting}
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Becomes the pool identifier (letters, digits, dashes; 3-40 chars).
                        </p>
                      </div>
                    </div>
                  </CollapsibleSection>

                  {/* MEMBERS — mirrors the drawer's "Members" section: pool
                      owner (CREATOR) + co-supervisors. Pre-filled (owner =
                      releaser), collapsed by default. */}
                  <CollapsibleSection
                    id="members"
                    title="Members"
                    subtitle="Pool owner · co-supervisors"
                    open={openSection.members}
                    onToggle={() => toggleSection('members')}
                    summary={
                      (ownerEmail || currentUserEmail || 'you')
                      + (supervisorEmails.length ? ` +${supervisorEmails.length}` : '')
                    }
                  >
                    <div className="space-y-4 py-1">
                      {/* Pool owner — single workspace member, defaults to the
                          releaser. Becomes the pool CREATOR; the only role that
                          can start a run at IDLE (with any co-supervisors). */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Pool owner
                        </label>
                        <p className="mt-0.5 text-xs text-gray-500">
                          Becomes the pool <span className="font-medium text-gray-700">CREATOR</span>.
                          They can manage members, change the trigger mode, and initiate runs from the room.
                        </p>
                        <div className="mt-2">
                          <SupervisorMemberPicker
                            tenantId={activeTenantId || ''}
                            value={ownerEmail}
                            onChange={(em) => setOwnerEmail(em)}
                          />
                        </div>
                      </div>
                      {/* Co-supervisors — multi workspace members. Approve /
                          deny / modify in the room; land in Members after
                          release for per-agent overrides. */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Co-supervisors
                          <span className="ml-2 text-xs font-normal text-gray-500">(optional)</span>
                        </label>
                        <p className="mt-0.5 text-xs text-gray-500">
                          Workspace members who can approve, deny or modify approvals in this pool.
                          Per-agent supervisor overrides land in <span className="font-medium">Members</span>{' '}
                          after release.
                        </p>
                        <div className="mt-2">
                          <WorkspaceMembersMultiPicker
                            tenantId={activeTenantId || ''}
                            value={supervisorEmails}
                            onChange={setSupervisorEmails}
                            excludeEmails={ownerEmail ? [ownerEmail] : []}
                            placeholder="Search workspace members…"
                            disabled={submitting}
                          />
                        </div>
                      </div>
                    </div>
                  </CollapsibleSection>

                  {/* TRIGGER MODE — mirrors the drawer's "Trigger mode"
                      section. Pre-filled (HYBRID), collapsed by default. */}
                  <CollapsibleSection
                    id="trigger"
                    title="Trigger mode"
                    subtitle="Who can start a run"
                    open={openSection.trigger}
                    onToggle={() => toggleSection('trigger')}
                    summary={
                      (triggerOptions.find((o) => o.value === triggerMode) || {}).label || triggerMode
                    }
                  >
                    <p className="mt-1 text-xs text-gray-500">
                      Who can start a run on this pool. Email approvals work in every mode.
                    </p>
                    <fieldset
                      className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3"
                      disabled={submitting}
                    >
                      <legend className="sr-only">Trigger mode</legend>
                      {triggerOptions.map((opt) => {
                        const checked = triggerMode === opt.value;
                        return (
                          <label
                            key={opt.value}
                            className={`flex flex-col gap-1 rounded-lg border p-3 text-sm cursor-pointer ${
                              checked
                                ? 'border-blue-400 bg-blue-50'
                                : 'border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <span className="flex items-center gap-2">
                              <input
                                type="radio"
                                name="release-trigger"
                                value={opt.value}
                                checked={checked}
                                onChange={() => setTriggerMode(opt.value)}
                              />
                              <span className="font-medium text-gray-900">{opt.label}</span>
                            </span>
                            <span className="text-xs text-gray-600">{opt.description}</span>
                          </label>
                        );
                      })}
                    </fieldset>
                  </CollapsibleSection>

                  {/* ROOM CONTEXT — mirrors the drawer's "Room context"
                      section: the room's shared setup (workspace bake +
                      own connections). Pre-filled (both on), collapsed. */}
                  <CollapsibleSection
                    id="roomContext"
                    title="Room context"
                    subtitle="Workspace · connections"
                    open={openSection.roomContext}
                    onToggle={() => toggleSection('roomContext')}
                    summary={[
                      hasBuilderAgent && bootstrapWorkspace ? 'Workspace' : null,
                      seedRoomTools ? 'Own connections' : 'Inherit connections',
                    ].filter(Boolean).join(' · ')}
                  >
                    <div className="space-y-3 py-1">
                      {/* Pre-bake the network's skills into a workspace for the
                          new room. Only for a network with a builder agent (a
                          workspace is the builder's tree). (SKILLS.md) */}
                      {hasBuilderAgent ? (
                        <label className="flex items-start gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={bootstrapWorkspace}
                            onChange={(e) => setBootstrapWorkspace(e.target.checked)}
                            disabled={submitting}
                            data-testid="release-bootstrap-workspace"
                            className="mt-0.5"
                          />
                          <span>
                            <span className="font-medium text-gray-900">Set up a workspace with the network's skills</span>
                            <span className="block text-xs text-gray-600">
                              Pre-loads this room with a workspace containing the network's skills, so builders are ready on the first run. You can edit or remove it later in the room's workspace.
                            </span>
                          </span>
                        </label>
                      ) : null}

                      {/* Give this room its own connection instances (MCP shells)
                          mirroring the network's tools. Default on; backend
                          no-ops when the network declares no MCPs. */}
                      <label className="flex items-start gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={seedRoomTools}
                          onChange={(e) => setSeedRoomTools(e.target.checked)}
                          disabled={submitting}
                          data-testid="release-seed-room-tools"
                          className="mt-0.5"
                        />
                        <span>
                          <span className="font-medium text-gray-900">Create this room's own connections</span>
                          <span className="block text-xs text-gray-600">
                            Adds this room's own copy of the network's tools (shared and per-agent), shown in the room's Connections tab to credential. Leave off to inherit the network's connections as-is.
                          </span>
                        </span>
                      </label>
                    </div>
                  </CollapsibleSection>
                </div>
              ) : (
                <div className="mt-4">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Select pools to patch</span>
                    {poolsLoading ? (
                      <span className="text-xs text-gray-500">Loading…</span>
                    ) : null}
                  </div>
                  {poolsError ? (
                    <p className="text-sm text-red-600">Could not load pools: {poolsError}</p>
                  ) : null}
                  {!poolsLoading && !poolsError && patchTargets.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      No existing pools to patch. Release to a new pool instead.
                    </p>
                  ) : null}
                  <ul className="max-h-48 space-y-1 overflow-y-auto">
                    {patchTargets.map((p) => {
                      const checked = selectedPoolIds.includes(p.poolId);
                      return (
                        <li key={p.poolId}>
                          <label
                            className={`flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm cursor-pointer ${
                              checked ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <span className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => togglePoolSelection(p.poolId)}
                                disabled={submitting}
                              />
                              <span className="font-medium text-gray-900">{p.name}</span>
                              <span className="text-gray-500">({p.poolId})</span>
                            </span>
                            <span className={`text-xs ${p.isBehind ? 'text-amber-700' : 'text-gray-500'}`}>
                              {displayVersionLabel(p, versions)}
                              {p.isBehind ? ` → ${patchTargetLabel}` : ''}
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
              {!error && submitDecision.reason ? (
                <p className="mt-3 text-xs text-gray-500">{submitDecision.reason}</p>
              ) : null}

              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                  onClick={() => !submitting && onClose()}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={WORKBENCH_OUTLINE_BTN_MODAL_LG}
                  disabled={submitting || !submitDecision.enabled}
                >
                  {submitting
                    ? (selectedBehindExisting ? 'Upgrading…' : 'Releasing…')
                    : (selectedBehindExisting ? `Upgrade → ${patchTargetLabel}` : 'Release')}
                </button>
              </div>
            </form>

            <div className="mt-6">
              <ReleaseVersionHistory networkId={networkId} />
            </div>
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>
  );
}
